import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { fmtDate, membershipLabel } from "@/lib/format";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { AdminPage, Card, Gate, H, KV, Meta, ReasonDialog, statusTone, useAdminMe } from "@/components/admin-ui";
import { Button, ChipGroup, ErrorState, Field, Loading, Pill, SectionLabel } from "@/components/ui";

const LABELS = { suspend: "Suspend", restrict: "Restrict", unrestrict: "Lift restriction", deactivate: "Deactivate", reactivate: "Reactivate" };

export default function AdminMemberDetail() {
  return <Gate perm="members" testId="admin-member-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const qc = useQueryClient();
  const { id } = useParams();
  const { data: me } = useAdminMe();
  const [action, setAction] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantPlan, setGrantPlan] = useState("Monthly");
  const [grantDays, setGrantDays] = useState("30");
  const [grantReason, setGrantReason] = useState("");
  const { data: m, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-member", id], queryFn: () => api(`/admin/members/${id}`) });
  if (isLoading) return <Loading />;
  if (isError || !m) return <ErrorState testId="admin-member-error" message={error?.message ?? ""} onRetry={() => refetch()} />;
  const canManage = me?.permissions.includes("members.manage");
  const st = m.account_status;
  const actions = [];
  if (st === "active") actions.push("restrict", "suspend");
  if (st === "restricted") actions.push("unrestrict", "suspend");
  if (canManage && ["active", "restricted", "suspended"].includes(st)) actions.push("deactivate");
  if (canManage && ["suspended", "deactivated"].includes(st)) actions.push("reactivate");
  const p = m.profile ?? {};

  const grant = async () => {
    const plan_id = grantPlan === "Free trial" ? "trial" : m.membership_plans.find((x) => x.name === grantPlan)?.id;
    try {
      await api(`/admin/members/${id}/membership`, { method: "POST", body: { plan_id, days: parseInt(grantDays, 10), reason: grantReason.trim() } });
      toast("Membership updated", "success");
      setGrantOpen(false);
      setGrantReason("");
      refetch();
    } catch (e) { toast(e.message, "error"); }
  };

  return (
    <AdminPage title="Member" testId="admin-member-screen">
      <Card testId="admin-member-summary">
        <div className="flex items-center gap-4">
          <img src={photoUri(m.main_photo_url)} alt="" className="h-20 w-20 rounded-full bg-[#F4EBE6] object-cover" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-[28px] leading-tight">{m.full_name}</p>
            <Meta>{m.display_name} · {m.age ?? "?"} · {m.city}, {m.country}</Meta>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Pill testId="admin-member-status" label={st.replace("_", " ")} tone={statusTone(st)} />
              {m.verification_status === "verified" ? <Pill label="Verified" tone="gold" icon={CheckCircle2} /> : <Pill label="Unverified" />}
              {m.is_test_profile ? <Pill label="Test profile" /> : null}
            </div>
          </div>
        </div>
      </Card>
      {actions.length ? (
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => <Button key={a} testId={`admin-member-action-${a}`} title={LABELS[a]} size="sm" variant={a === "suspend" || a === "deactivate" ? "danger" : "outline"} onClick={() => setAction(a)} />)}
        </div>
      ) : null}
      {m.photos.length ? (
        <div className="no-scrollbar flex gap-2 overflow-x-auto">{m.photos.map((u) => <img key={u} src={photoUri(u)} alt="" className="h-40 w-32 shrink-0 rounded-xl object-cover" />)}</div>
      ) : null}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card testId="admin-member-membership">
          <KV k="Membership" v={m.membership ? membershipLabel(m.membership) : "—"} testId="admin-member-membership-label" />
          <KV k="Ends" v={fmtDate(m.membership?.ends_at)} />
          {m.membership_payments.map((x) => <KV key={x.reference} k={x.reference} v={`${x.plan_name ?? ""} ₦${x.amount.toLocaleString()} · ${x.status}`} />)}
          {canManage ? <Button testId="admin-member-grant-button" title="Grant / extend membership" size="sm" variant="outline" className="mt-3" onClick={() => setGrantOpen(true)} /> : null}
        </Card>
        <Card testId="admin-member-account">
          <KV k="Email" v={m.email} /><KV k="Phone" v={m.phone_masked} /><KV k="Mission" v={m.mission} />
          <KV k="Registered" v={fmtDate(m.created_at)} /><KV k="Last active" v={fmtDate(m.last_active_at)} />
          <KV k="Profile complete" v={m.onboarding_complete ? "Yes" : "No"} /><KV k="Community group" v={m.group_name} />
          <KV k="Income" v={m.income_disclosed ? "Disclosed (hidden)" : "Not given"} />
        </Card>
        <Card testId="admin-member-profile">
          {["gender", "occupation", "education", "marital_status", "preferred_language", "nationality"].map((k) => <KV key={k} k={k.replace("_", " ")} v={p[k]} />)}
          <p className="mt-2 text-sm">{p.bio}</p>
        </Card>
        <Card testId="admin-member-stats">
          {Object.entries(m.stats).map(([k, v]) => <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} testId={`admin-member-stat-${k}`} />)}
        </Card>
      </div>
      <H>History</H>
      {m.history.length ? m.history.map((h, i) => (
        <Card key={i} testId={`admin-member-history-${i}`}>
          <p className="font-bold capitalize">{h.action.replace(/_/g, " ")}</p>
          <Meta>{h.actor_name} · {fmtDate(h.created_at)}</Meta>
          {h.reason ? <Meta>Reason: {h.reason}</Meta> : null}
        </Card>
      )) : <Meta>No staff actions yet.</Meta>}
      <Modal open={grantOpen} onClose={() => setGrantOpen(false)} title="Grant / extend membership" testId="admin-grant-sheet">
        <Meta>Adds days from the current end date (or today if expired). No payment is recorded; the reason is audited.</Meta>
        <div className="mt-4"><SectionLabel>Plan</SectionLabel></div>
        <ChipGroup testIdPrefix="admin-grant-plan" multi={false} options={["Free trial", ...m.membership_plans.map((x) => x.name)]} value={[grantPlan]} onChange={(v) => v[0] && setGrantPlan(v[0])} />
        <Field className="mt-4" testId="admin-grant-days-input" label="Days to add" inputMode="numeric" value={grantDays} onChange={(e) => setGrantDays(e.target.value)} />
        <Field testId="admin-grant-reason-input" label="Reason (required)" multiline value={grantReason} onChange={(e) => setGrantReason(e.target.value)} />
        <Button testId="admin-grant-submit" title="Grant membership" className="w-full" disabled={!(parseInt(grantDays, 10) > 0) || grantReason.trim().length < 3} onClick={grant} />
      </Modal>
      <ReasonDialog open={!!action} testId="admin-member-reason-sheet" title={action ? `${LABELS[action]} ${m.display_name}` : ""} confirmLabel={action ? LABELS[action] : ""}
        danger={action === "suspend" || action === "deactivate"} placeholder="Reason (shared with the member)" onClose={() => setAction(null)}
        onConfirm={async (reason) => {
          try {
            await api(`/admin/members/${id}/status`, { method: "POST", body: { action, reason } });
            toast("Member updated", "success");
            setAction(null);
            refetch();
            qc.invalidateQueries({ queryKey: ["admin-members"] });
          } catch (e) { toast(e.message, "error"); }
        }} />
    </AdminPage>
  );
}
